<template>
  <!-- Add a system in a side panel (US 9370), from the systems overview, an activity's systems, or /systems/create.
       The logo can be uploaded or found with AI straight away; it's saved once the system exists. -->
  <V2SidePanel ref="panel"
               :kind="$t('system_hero.panel.kind')"
               :title="$t('system_create.title')"
               :intro="$t('system_create.intro')"
               :busy="saving"
               qid="system-create-panel"
               @hidden="reset">
    <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>

    <div class="v2-field">
      <label class="v2-field__label" for="sx-create-name">{{ $t('system_create.name') }} <span class="v2-field__req">*</span></label>
      <b-form-input id="sx-create-name"
                    ref="name"
                    v-model="form.name"
                    :maxLength="255"
                    autocomplete="off"
                    :placeholder="$t('system_create.name_placeholder')"
                    qid="create-s-form-name"
                    @input="checkName"></b-form-input>
      <div v-if="exact" class="sx-create__same" qid="system-create-exists">
        <font-awesome-icon icon="exclamation-triangle"/>
        <span>{{ $t('system_create.exists') }}</span>
        <router-link :to="{ name: 'systems.view', params: { slug: exact.slug } }" @click.native="$refs.panel.hide()">{{ exact.name }}</router-link>
      </div>
      <small v-else-if="similar.length" class="v2-field__note">
        {{ $t('system_create.similar') }}
        <template v-for="(item, index) in similar">
          <router-link :key="item.id" :to="{ name: 'systems.view', params: { slug: item.slug } }" @click.native="$refs.panel.hide()">{{ item.name }}</router-link><template v-if="index < similar.length - 1">, </template>
        </template>
      </small>
    </div>

    <!-- Logo: kept here until the system is created -->
    <div class="v2-field" qid="system-create-logo">
      <span class="v2-field__label">{{ $t('system_logo.label') }} <span class="v2-field__hint">· {{ $t('risk_tab.optional') }}</span></span>
      <div class="sx-logo">
        <span class="sx-logo__tile" :class="{ 'has-image': logo }">
          <img v-if="logo" :src="logo.image" alt="">
          <template v-else>{{ initials }}</template>
        </span>
        <div class="sx-logo__actions">
          <label class="v2-link sx-logo__upload" qid="system-create-logo-upload">
            <font-awesome-icon icon="cloud-upload-alt"/>{{ $t('system_logo.upload') }}
            <input type="file" accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml" class="d-none" @change="chooseFile">
          </label>
          <button v-if="getClient.aiModule" type="button" class="v2-link sx-logo__ai" :disabled="finding || !form.name.trim()" :title="form.name.trim() ? '' : $t('system_create.name_first')" qid="system-create-logo-find" @click="findLogo">
            <font-awesome-icon :icon="finding ? 'spinner' : 'sparkles'" :class="{ 'fa-spin': finding }"/>{{ finding ? $t('system_logo.finding') : $t('system_logo.find') }}
          </button>
          <button v-if="logo" type="button" class="v2-link is-danger" @click="logo = null">
            <font-awesome-icon icon="times"/>{{ $t('system_logo.remove') }}
          </button>
        </div>
      </div>
      <div v-if="candidates" class="sx-logo__found">
        <small class="v2-field__note mt-0">{{ candidates.length ? $t('system_logo.pick', { vendor: vendor || form.name }) : $t('system_logo.none') }}</small>
        <div class="sx-logo__grid">
          <button v-for="(candidate, index) in candidates"
                  :key="index"
                  type="button"
                  class="sx-logo__candidate"
                  :class="{ 'is-on': logo && logo.source === candidate.source }"
                  :title="candidate.source"
                  :qid="'system-create-logo-candidate-' + index"
                  @click="pick(candidate)">
            <img :src="candidate.image" alt="">
            <small>{{ host(candidate.source) }}</small>
          </button>
        </div>
      </div>
    </div>

    <div class="v2-field">
      <label class="v2-field__label" for="sx-create-description">{{ $t('systems.create.description_label') }}</label>
      <b-form-textarea id="sx-create-description" v-model="form.description" rows="2" max-rows="6" :placeholder="$t('system_create.description_placeholder')" qid="create-s-form-description"></b-form-textarea>
    </div>

    <div class="v2-field__two">
      <div class="v2-field">
        <label class="v2-field__label">{{ $t('systems_overview.missing.owner') }} <span class="v2-field__hint">{{ $t('system_hero.panel.owner_hint') }}</span></label>
        <multiselect v-model="owner"
                     :options="users"
                     track-by="id"
                     label="name"
                     :placeholder="$t('system_hero.panel.owner_placeholder')"
                     :select-label="$t('system.dropdown_select_label')"
                     :selected-label="$t('system.dropdown_selected_label')"
                     :deselect-label="$t('system.dropdown_deselect_label')"
                     qid="system-create-owner"
                     @search-change="loadUsers"></multiselect>
      </div>
      <div class="v2-field">
        <label class="v2-field__label">{{ $t('system_hero.panel.contributors') }}</label>
        <multiselect v-model="contributors"
                     :options="contributorOptions"
                     :multiple="true"
                     track-by="id"
                     label="name"
                     :placeholder="$t('system_hero.panel.contributors_placeholder')"
                     :select-label="$t('system.dropdown_select_label')"
                     :selected-label="$t('system.dropdown_selected_label')"
                     :deselect-label="$t('system.dropdown_deselect_label')"
                     qid="system-create-contributors"
                     @search-change="loadUsers"></multiselect>
      </div>
    </div>
    <small v-if="!owner && !contributors.length" class="v2-field__note">{{ $t('systems_overview.create_hint') }}</small>

    <div class="v2-field">
      <span class="v2-field__label">{{ $t('systems_overview.fields.runs_as') }}</span>
      <div class="sp-runs" role="radiogroup">
        <button v-for="value in runsAsValues"
                :key="value"
                type="button"
                role="radio"
                :aria-checked="form.runs_as === value ? 'true' : 'false'"
                :class="{ on: form.runs_as === value }"
                :qid="'system-create-runs-as-' + value"
                @click="form.runs_as = form.runs_as === value ? null : value">{{ $t('systems_overview.runs_as.' + value) }}</button>
      </div>
      <small class="v2-field__note">{{ needsLocation ? $t('system_page.where.help_premises') : (getClient.contractBasicModule ? $t('system_page.where.help') : $t('system_page.where.help_basic')) }}</small>
    </div>

    <div v-if="needsLocation" class="v2-field">
      <label class="v2-field__label" for="sx-create-location">{{ $t('systems_overview.fields.location') }}</label>
      <b-form-input id="sx-create-location" v-model="form.location" :maxLength="255" :placeholder="$t('system_page.where.location_placeholder')" qid="system-create-location"></b-form-input>
    </div>

    <div class="v2-field__two">
      <div v-if="getClient.gdprModule" class="v2-field">
        <span class="v2-field__label">{{ $t('system_page.data.structure') }}</span>
        <div class="sx-toggles">
          <button v-for="key in structureKeys"
                  :key="key"
                  type="button"
                  :aria-pressed="form[key] ? 'true' : 'false'"
                  :class="{ on: form[key] }"
                  @click="form[key] = form[key] ? 0 : 1">
            <font-awesome-icon v-if="form[key]" icon="check"/>{{ $t('systems.table.' + key.replace('is_', '')) }}
          </button>
        </div>
      </div>
      <div class="v2-field">
        <label class="v2-field__label" for="sx-create-since">{{ $t('system_hero.panel.in_use_since') }}</label>
        <b-form-input id="sx-create-since" v-model="form.in_use_since" type="number" min="1950" :max="maxYear" :placeholder="$t('system_hero.panel.in_use_since_placeholder')"></b-form-input>
      </div>
    </div>

    <div class="v2-field">
      <label class="v2-field__label">{{ $t('systems.create.tags_label') }}</label>
      <multiselect v-model="tags"
                   :options="tagOptions"
                   :multiple="true"
                   :taggable="true"
                   track-by="name_translated"
                   label="name_translated"
                   :placeholder="$t('systems.create.tags_placeholder')"
                   :select-label="$t('system.dropdown_select_label')"
                   :selected-label="$t('system.dropdown_selected_label')"
                   :deselect-label="$t('system.dropdown_deselect_label')"
                   qid="system-create-tags"
                   @search-change="loadTags"
                   @tag="addTag"></multiselect>
    </div>

    <template #foot>
      <span class="v2-panel__grow"></span>
      <b-button variant="secondary" :disabled="saving || !canSave" qid="system-create-another" @click="save('another')">
        {{ $t('system_create.save_another') }}
      </b-button>
      <b-button variant="success" :disabled="saving || !canSave" qid="create-s-submit-button" @click="save(openAfter ? 'open' : 'close')">
        <font-awesome-icon :icon="saving ? 'spinner' : 'check'" :class="{ 'fa-spin': saving }"/>
        {{ openAfter ? $t('system_create.save_open') : $t('system_create.save') }}
      </b-button>
    </template>
  </V2SidePanel>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import V2SidePanel from '@/components/V2SidePanel.vue';
  import {visibleOnly, hostOf} from '@/util/logoImage.js';

  const MAX_LOGO_BYTES = 512000;

  export default {
    name: 'SystemCreatePanel',
    components: {
      V2SidePanel
    },
    props: {
      // Open the new system afterwards (the systems overview); elsewhere, e.g. an activity, stay where you are
      openAfter: { type: Boolean, default: true }
    },
    data: function() {
      return {
        form: this.emptyForm(),
        owner: null,
        contributors: [],
        tags: [],
        users: [],
        tagOptions: [],
        exact: null,
        similar: [],
        // { image: data URI, file: File } for an upload, or { image, source } for a logo found by AI
        logo: null,
        candidates: null,
        vendor: null,
        finding: false,
        saving: false,
        error: '',
        nameTimer: null,
        runsAsValues: ['premises', 'hosted', 'saas', 'physical'],
        structureKeys: ['is_structured', 'is_unstructured', 'is_physical'],
        maxYear: new Date().getFullYear() + 1
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      needsLocation() {
        return ['premises', 'physical'].includes(this.form.runs_as);
      },
      contributorOptions() {
        return this.owner ? this.users.filter(user => user.id !== this.owner.id) : this.users;
      },
      canSave() {
        return !!this.form.name.trim() && !this.exact;
      },
      initials() {
        let words = this.form.name.trim().split(/\s+/).filter(Boolean);
        return words.length > 1 ? (words[0][0] + words[1][0]).toUpperCase() : (words[0] || '?').slice(0, 3);
      }
    },
    methods: {
      host: hostOf,
      emptyForm() {
        return { name: '', description: '', runs_as: null, location: '', in_use_since: '', is_structured: 0, is_unstructured: 0, is_physical: 0 };
      },
      show(name) {
        this.reset();
        this.form.name = name || '';
        this.loadUsers();
        this.loadTags();
        this.$refs.panel.show();
        let self = this;
        setTimeout(function() {
          if (self.$refs.name) {
            self.$refs.name.focus();
          }
        }, 300);
      },
      reset() {
        Object.assign(this, { form: this.emptyForm(), owner: null, contributors: [], tags: [], exact: null, similar: [], logo: null,
          candidates: null, vendor: null, finding: false, saving: false, error: '' });
      },
      // An existing system with the same name stops the create; similar names are only mentioned
      checkName() {
        clearTimeout(this.nameTimer);
        this.nameTimer = setTimeout(this.loadMatches, 300);
      },
      loadMatches() {
        let self = this;
        let name = this.form.name.trim();
        if (name.length < 2) {
          this.exact = null;
          this.similar = [];
          return;
        }
        piincHttp.get('systems', { params: { client: this.getClient.slug, keyword: name, per_page: 6 } }).then(function(response) {
          if (self.form.name.trim() !== name) {
            return;
          }
          let items = response.data.items || [];
          self.exact = items.find(item => item.name.trim().toLowerCase() === name.toLowerCase()) || null;
          self.similar = items.filter(item => item !== self.exact).slice(0, 3);
        }, function() {});
      },
      chooseFile(event) {
        let self = this;
        let file = event.target.files[0];
        event.target.value = '';
        if (!file) {
          return;
        }
        if (file.size > MAX_LOGO_BYTES) {
          this.error = this.$t('system_logo.invalid');
          return;
        }
        let reader = new FileReader();
        reader.onload = function() {
          self.logo = { image: reader.result, file: file };
          self.candidates = null;
        };
        reader.readAsDataURL(file);
      },
      findLogo() {
        let self = this;
        this.finding = true;
        this.error = '';
        piincHttp.post('systems/logo/find', { client: this.getClient.slug, name: this.form.name.trim(), description: this.form.description || null }).then(function(response) {
          self.vendor = response.data.vendor;
          return visibleOnly(response.data.candidates).then(function(candidates) {
            self.finding = false;
            self.candidates = candidates;
          });
        }, function(error) {
          self.finding = false;
          self.error = (error.data && error.data.message) || self.$t('system_logo.ai_failed');
        });
      },
      pick(candidate) {
        this.logo = { image: candidate.image, source: candidate.source };
      },
      addTag(name) {
        let tag = { name: name, name_translated: name };
        this.tags.push(tag);
        this.tagOptions.push(tag);
      },
      payload() {
        let users = (this.owner ? [this.owner.id] : []).concat(this.contributors.filter(user => !this.owner || user.id !== this.owner.id).map(user => user.id));
        let data = {
          client: this.getClient.slug,
          name: this.form.name.trim(),
          description: this.form.description || '',
          runs_as: this.form.runs_as || null,
          location: this.needsLocation ? (this.form.location.trim() || null) : null,
          in_use_since: this.form.in_use_since ? Number(this.form.in_use_since) : null,
          is_structured: this.form.is_structured,
          is_unstructured: this.form.is_unstructured,
          is_physical: this.form.is_physical,
          tags: this.tags.map(tag => tag.name_translated)
        };
        if (users.length) {
          data.users = users;
        }
        if (this.owner) {
          data.owner = this.owner.id;
        }
        return data;
      },
      // The logo goes on the new system right after it's created
      saveLogo(system) {
        if (!this.logo) {
          return Promise.resolve();
        }
        if (this.logo.file) {
          let data = new FormData();
          data.append('file', this.logo.file);
          return piincHttp.post('systems/' + system.slug + '/logo', data, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
        return piincHttp.post('systems/' + system.slug + '/logo', { image: this.logo.image, source: this.logo.source });
      },
      // then: open (the new system), close (stay where you are) or another (empty panel for the next one)
      save(then) {
        let self = this;
        if (!this.owner && this.contributors.length) {
          this.error = this.$t('system.owners_error_message');
          return;
        }
        this.saving = true;
        this.error = '';
        let created = null;
        piincHttp.post('systems', this.payload())
          .then(function(response) {
            created = response.data;
            return self.saveLogo(created).catch(function() {
              self.$toastr('warning', self.$t('system_create.logo_failed'));
            });
          })
          .then(function() {
            self.saving = false;
            self.$toastr('success', self.$t('systems.create.success'));
            self.$emit('created', created);
            if (then === 'another') {
              self.reset();
              self.$refs.name.focus();
            } else {
              self.$refs.panel.hide();
              if (then === 'open') {
                self.$router.push({ name: 'systems.view', params: { slug: created.slug } });
              }
            }
          }, function(error) {
            self.saving = false;
            self.error = (error && error.data && error.data.message) || self.$t('system_hero.panel.error');
          });
      },
      loadUsers(query) {
        let self = this;
        piincHttp.get('users', { params: { client: this.getClient.id, keyword: query || undefined, per_page: 50 } }).then(function(response) {
          self.users = response.data.items;
        }, function() {});
      },
      loadTags(query) {
        let self = this;
        piincHttp.get('tags', { params: { client: this.getClient.slug, keyword: query || undefined } }).then(function(response) {
          self.tagOptions = response.data;
        }, function() {});
      }
    }
  }
</script>
